import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link as MuiLink, Paper, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useCurrentUser } from '../../auth/useCurrentUser';
import { useAppLogout } from '../../auth/useAppLogout';
import axiosClient from '../../api/axiosClient';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../theme/tokens';

/**
 * Stand-alone page shown when `/api/me` returns 403 — OR when navigated to
 * directly with `location.state.reason` set (RBAC_V2's `LandingRedirect` /
 * `TenantIndexRedirect` / `RequirePermission`'s fallback chain; these
 * reasons are unrelated to `/api/me`, so they aren't among
 * `noAppAccessReason`'s codes). Mounted outside of AdminLayout and
 * TenantLayout so the user doesn't see broken chrome.
 *
 * Behavior:
 *  - `no_app_access`: red lock icon + ask-a-super-admin message
 *  - `email_not_verified`: amber email icon + verify-then-retry message
 *  - `no_tenant_access` (router state): red lock icon + ask-a-super-admin
 *    message, scoped to tenant access rather than app access (design §6.3 —
 *    a `hide_chrome` holder with zero accessible tenants)
 *  - `no_surfaces` (router state): red lock icon + ask-a-super-admin
 *    message, for an identity with SOME tenant access but a role that
 *    grants zero portal surfaces (the fallback chain's own zero-permission
 *    terminus — reachable via `firstGrantedSurfacePath` returning
 *    `/access-denied`)
 *
 * Both `/api/me`-driven variants expose a "Try again" button that
 * invalidates the `['me']` query and lets the user fall back into the
 * normal flow once their state is fixed backend-side (role granted / email
 * confirmed). Kept for the router-state variants too — harmless if it can't
 * help (re-fetching `/api/me` doesn't grant tenant access or surfaces), and
 * consistent with every other reason on this page.
 */
const AccessDenied: React.FC = () => {
  const { email, noAppAccessReason } = useCurrentUser();
  const location = useLocation();
  const stateReason = (location.state as { reason?: string } | null)?.reason;
  const noTenantAccess = stateReason === 'no_tenant_access';
  const noSurfaces = stateReason === 'no_surfaces';
  const queryClient = useQueryClient();
  const logout = useAppLogout();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      // Verify-email variant: force the server to re-read email_verified from
      // Auth0 (bypass its 5-min /userinfo cache) so a user who JUST confirmed
      // their email is unblocked now, not after the cache TTL. Best-effort — a
      // still-unverified user 403s here; the refetch below reflects the truth.
      if (noAppAccessReason === 'email_not_verified') {
        try {
          await axiosClient.get('/api/me?refreshProfile=1');
        } catch {
          /* still unverified — fall through to the normal refetch */
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.refetchQueries({ queryKey: ['me'] });
    } finally {
      // 1s visible spinner so the user sees we did something — the state
      // may not actually change if the root cause isn't fixed yet.
      setTimeout(() => setRetrying(false), 1000);
    }
  };

  const isEmailVariant = noAppAccessReason === 'email_not_verified';

  // Default to the lock variant for either `no_app_access` or an unknown
  // reason — reaching this page without a reason is itself an anomaly and
  // the safe display is "you don't have access" rather than a blank screen.

  const Icon = isEmailVariant ? MarkEmailReadIcon : LockIcon;
  const iconColor = isEmailVariant ? colors.warningIcon : colors.errorIcon;
  const iconBg = isEmailVariant ? colors.warningBg : colors.errorBg;
  const title = noSurfaces
    ? 'No portal access'
    : noTenantAccess
      ? 'No tenant access'
      : isEmailVariant
        ? 'Verify your email'
        : 'Access denied';
  const body = noSurfaces
    ? "Your account is active but your role doesn't grant any portal sections yet. Ask a super-admin to update your role."
    : noTenantAccess
      ? `Your account isn't granted access to any tenant yet. Ask a super-admin to grant ${email || 'your account'} access to a tenant.`
      : isEmailVariant
        ? `We sent a verification link to ${email || 'your inbox'}. Confirm it, then press Try again.`
        : `Your account isn't authorized for this app. Ask a super-admin to add ${email || 'your email'}.`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bgPage,
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 480,
          borderRadius: '16px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
          p: 5,
          textAlign: 'center',
        }}
        role="alert"
        aria-live="polite"
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: iconBg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
          }}
        >
          <Icon sx={{ fontSize: 36, color: iconColor }} />
        </Box>

        <Typography sx={{ ...typo.pageTitle, fontSize: 24, mb: 1.5 }}>{title}</Typography>
        <Typography sx={{ ...typo.secondaryText, mb: 4, lineHeight: 1.6 }}>{body}</Typography>

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={retrying}
            sx={primaryButtonSx}
            startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {retrying ? 'Checking…' : 'Try again'}
          </Button>
          <Button variant="outlined" onClick={logout} sx={secondaryButtonSx}>
            Sign out
          </Button>
        </Box>

        <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
          Contact{' '}
          <MuiLink
            href="mailto:platform-support@sunnybenefits.com"
            sx={{ color: colors.adminAccent, fontWeight: 600 }}
          >
            platform-support@sunnybenefits.com
          </MuiLink>
        </Typography>
      </Paper>
    </Box>
  );
};

export default AccessDenied;
